import { KeyboardEvent, useRef } from "react";
import { useTranslation } from "react-i18next";

import type { ReviewVerdict } from "@/redux/certifications/types";
import {
  REVIEW_VERDICT_LABEL_KEYS,
  REVIEW_VERDICTS,
} from "@/domains/certifications/constants";
import { AnswerVerdictTagProps } from "@/domains/certifications/components/types";

import "./styles.css";

const ARROW_KEYS = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"];

const AnswerVerdictTag = ({
  value,
  aiVerdict,
  onChange,
  readOnly = false,
  labelledBy,
}: AnswerVerdictTagProps) => {
  const { t } = useTranslation();
  const optionRefs = useRef<Partial<Record<ReviewVerdict, HTMLButtonElement>>>(
    {},
  );

  const aiMarker = (verdict: ReviewVerdict) =>
    verdict === aiVerdict && (
      <span className="answer-verdict-ai">
        {t("certification.labels.verdict.aiReviewed")}
      </span>
    );

  if (readOnly) {
    return value ? (
      <span
        className={`answer-verdict-option answer-verdict-${value.toLowerCase()} answer-verdict-selected answer-verdict-static body-s-regular`}
      >
        {t(REVIEW_VERDICT_LABEL_KEYS[value])}
        {aiMarker(value)}
      </span>
    ) : null;
  }

  const handleKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    focused: ReviewVerdict,
  ) => {
    if (!ARROW_KEYS.includes(event.key)) {
      return;
    }
    event.preventDefault();
    const index = REVIEW_VERDICTS.indexOf(focused);
    const step = event.key === "ArrowLeft" || event.key === "ArrowUp" ? -1 : 1;
    const next =
      REVIEW_VERDICTS[
        (index + step + REVIEW_VERDICTS.length) % REVIEW_VERDICTS.length
      ];
    if (next) {
      onChange?.(next);
      optionRefs.current[next]?.focus();
    }
  };

  return (
    <div
      role="radiogroup"
      aria-labelledby={labelledBy}
      className="answer-verdict flex"
    >
      {REVIEW_VERDICTS.map((verdict, index) => {
        const isSelected = verdict === value;
        const isTabStop = value ? isSelected : index === 0;

        return (
          <button
            key={verdict}
            ref={(element) => {
              if (element) {
                optionRefs.current[verdict] = element;
              }
            }}
            type="button"
            role="radio"
            aria-checked={isSelected}
            tabIndex={isTabStop ? 0 : -1}
            className={`answer-verdict-option answer-verdict-${verdict.toLowerCase()} body-s-regular ${
              isSelected ? "answer-verdict-selected" : ""
            }`}
            onClick={() => onChange?.(verdict)}
            onKeyDown={(event) => handleKeyDown(event, verdict)}
          >
            {t(REVIEW_VERDICT_LABEL_KEYS[verdict])}
            {aiMarker(verdict)}
          </button>
        );
      })}
    </div>
  );
};

export default AnswerVerdictTag;
